export type HistoryStackOptions<T> = {
  maxLength: number;
  minUndoDepth?: number;
  useStackCurrent?: boolean;
  isEqual?: (a: T, b: T) => boolean;
  clone?: (value: T) => T;
};

export class HistoryStack<T> {
  private undoStack: T[] = [];
  private redoStack: T[] = [];
  private readonly maxLength: number;
  private readonly minUndoDepth: number;
  private readonly useStackCurrent: boolean;
  private readonly isEqual: ((a: T, b: T) => boolean) | null;
  private readonly clone: (value: T) => T;

  constructor(options: HistoryStackOptions<T>) {
    this.maxLength = options.maxLength;
    this.minUndoDepth = options.minUndoDepth ?? 0;
    this.useStackCurrent = options.useStackCurrent ?? false;
    this.isEqual = options.isEqual ?? null;
    this.clone = options.clone ?? ((value: T): T => value);
  }

  ensureSnapshot(snapshot: T): void {
    if (this.undoStack.length === 0) {
      this.undoStack = [this.clone(snapshot)];
      this.redoStack = [];
    }
  }

  record(snapshot: T): void {
    const last = this.undoStack[this.undoStack.length - 1];
    if (last !== undefined && this.isEqual?.(snapshot, last)) {
      return;
    }

    this.undoStack.push(this.clone(snapshot));
    this.redoStack = [];

    if (this.undoStack.length > this.maxLength) {
      this.undoStack.shift();
    }
  }

  undo(current?: T): T | null {
    if (!this.canUndo()) return null;

    if (this.useStackCurrent) {
      const active = this.undoStack.pop()!;
      this.redoStack.push(active);
      return this.undoStack[this.undoStack.length - 1] ?? null;
    }

    if (current === undefined) return null;
    this.redoStack.push(this.clone(current));
    return this.undoStack.pop() ?? null;
  }

  redo(current?: T): T | null {
    if (!this.canRedo()) return null;

    if (this.useStackCurrent) {
      const next = this.redoStack.pop()!;
      this.undoStack.push(next);
      return next;
    }

    if (current === undefined) return null;
    this.undoStack.push(this.clone(current));
    return this.redoStack.pop() ?? null;
  }

  canUndo(): boolean {
    return this.undoStack.length > this.minUndoDepth;
  }

  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  load(undoStack: T[] = [], redoStack: T[] = []): void {
    this.undoStack = [...undoStack];
    this.redoStack = [...redoStack];
  }

  getUndoStack(): T[] {
    return [...this.undoStack];
  }

  getRedoStack(): T[] {
    return [...this.redoStack];
  }
}
